<template>
    <div class="page privacy legal">

        <h1>Unpaywall privacy policy</h1>
        Last Revised: November 5, 2020<br>
        <br>
        Unpaywall is a website featuring web-based tools created and maintained by Impactstory, Inc., doing business as OurResearch (the
        "<u>Company</u>," "<u>we</u>," "<u>ours</u>," "<u>us</u>"). ImpactStory respects and values your privacy, and we
        are committed to protecting it through our compliance with this privacy policy ("<u>Policy</u>"). This Policy
        describes the types of information we may collect from you when you visit <a href="https://unpaywall.org"
                                                                                     target="_blank">https://unpaywall.org</a>
        (the "<u>Site</u>," a component of the Service), when you use or interact with the API, or when you use or
        interact with any other products, applications, or features provided by us (collectively, the "<u>Service</u>"),
        as well as our practices for collecting, using, maintaining, protecting, and disclosing that information. Any
        capitalized terms used but not defined in this Policy are defined in our Terms of Service. <br>
        <br>
        If you do not agree with this Policy, or our other policies and practices, you should not use the Service. This
        Policy may change from time to time. We will notify you of material changes, but it is your responsibility to
        check this Policy periodically for updates.<br>

        <h2>How this Privacy Policy Applies</h2>

        This Policy applies to information we collect: (1) on this Site; (2) in any electronic communication between you
        and the Service; (3) through the API or other web-based tools listed on the Site and maintained by Impactstory,
        and (4) when you interact with our applications (such as our web-browser extension) on third-party websites and
        services.<br><br>
        This Policy does NOT apply to information collected by: (1) us offline or through any other means, including on
        any other website or service operated by any third party (including our affiliates and subsidiaries); or (2) any
        third party (including our affiliates and subsidiaries), including through any application, website, or content
        that may link to or be accessible from or through the Service.<br>

        <h2>Children Under the Age of 13</h2>

        The Service is not intended for persons under the age of 13, and persons under the age of 13 are not permitted
        to use the Service. We do not knowingly collect personal information from persons under the age of 13. If you
        are under 13, do not use or provide any information on this Site or attempt to register or use any features of
        the Service. We encourage parents and legal guardians to monitor their children's Internet usage and to help
        enforce our privacy policy by instructing their children never to provide personal information on the Site. If
        you have reason to believe that a child has provided personal information through the Site, please contact us by
        email at <a href="mailto:team@ourresearch.org">team@ourresearch.org</a>. If we learn we have collected or
        received personal information from a child under 13 without verification of parental consent, we will delete
        that information.<br>

        <h2 id="types">Types of Information We Collect & How We Collect It</h2>

        Depending on how you use the Service, we, along with any third-party companies or individuals with whom we work,
        may collect information directly and voluntarily from you when you use the Service or contact us directly. We
        may collect Personal Information and Non-Personal Information, depending on how you use the Service and what
        information you provide to us. <br><br>
        <b>We do not collect any Personal Information when you use the Browser Extension.</b> We collect Personal
        Information described below when you subscribe to the Data Feed; provide billing information and authorize
        payment(s) for a Data Feed subscription; email us or send a message through the electronic forms provided on the
        Site; provide your email address as part of an API call; provide your email address in order to use a web-based
        query tool such as our Simple Query Tool (<a href="https://unpaywall.org/products/simple-query-tool"
                                                     target="_blank">https://unpaywall.org/products/simple-query-tool</a>);
        enter a contest or promotion sponsored by us; report a problem with our Service; or respond to polls or surveys
        distributed for research purposes.<br><br>
        <b>Personal Information.</b> Personal Information is generally any information that can be used to identify you
        individually, either by itself or in conjunction with other information.<br><br>
        Personal Information may include, without limitation:<br>
        <ul type="disc">
            <li>Contact Information, such as your name and email address</li>
            <li>Information about the organization for which you work;</li>
            <li>Demographic or location information, when that information is linked to other Personal Information or
                Non-Personal Information that allows an individual to be identified;
            </li>
            <li>Financial information, billing preferences, and your unique API key.</li>
        </ul>
        <b>Non-Personal Information.</b> Non-Personal Information includes information that is about you or about your
        use of the Service but does not identify you personally, such as:<br>
        <ul type="disc">
            <li>IP addresses</li>
            <li>Anonymous usage data;</li>
            <li>Referring/exit pages and URLs;</li>
            <li>Browser or platform type;</li>
            <li>Equipment used to access the Service;</li>
            <li>Language preferences;</li>
            <li>Preferences you submit and preferences that are generated based on data you submit; and</li>
            <li>The date and time of user requests.</li>
        </ul>

        <b>How We Collect Non-Personal Information.</b> We generally collect Non-Personal Information through automatic
        data collection technologies, which are described in more detail below. <br>
        <br>
        <b>*The preceding lists of Personal Information and Non-Personal Information we may collect are illustrative,
            not exhaustive.</b><br>

        <h2>Automatic Data Collection and How We Use It</h2>

        <b>We do not collect Personal Information from users of the Browser Extension.</b> When you use the Browser
        Extension and view a page in your browser that we recognize to be a scholarly article, we send the DOI (a short
        unique ID for every scholarly article) of that article to our servers. <b>The Browser Extension does not use
        beacons, cookies or other automatic data collection technologies to track information.</b><br>
        <br>
        <b>For components of the Service other than the Browser Extension</b>, we may use automatic data collection
        technologies and analytics tools (such as Google Analytics) to collect information from your device or browser
        about your equipment, browsing actions, and patterns when you access, use, and interact with the Service. The
        information we collect automatically is statistical data and does not include Personal Information. We use this
        information to maintain the security and operation of the Service, and to gain insights about the functionality
        and use of the Service in order to improve the Service. <br>
        <br>
        The technologies we use for the automatic data collection described above may include:<br>
        <ul type="disc">
            <li><b>Cookies (or browser cookies).</b> A cookie is a small file placed on the hard drive of your computer.
                You may refuse to accept browser cookies by activating the appropriate setting on your browser. Unless
                you have adjusted your browser setting so that it will refuse cookies, our system will issue cookies
                when you direct your browser to the Site.
            </li>
            <li><b>Web Beacons.</b> Components of the Service and our e-mails may contain small electronic files known
                as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit the
                Company, for example, to count users who have visited those pages or opened an email and for other
                related website statistics (for example, recording the popularity of certain website content and
                verifying system and server integrity).
            </li>
        </ul>

        <h2>Location Information</h2>

        We may, from time to time, track use location through IP logging for security and fraud prevention purposes. Use
        location may also be tracked in connection with our use of automatic data collection technologies.<br>

        <h2>Do Not Track Disclosure</h2>

        We do not track information about users or visitors to the Site over time and across third party websites to
        provide targeted advertising, so we do not respond to Do Not Track (DNT) signals. However, some third party
        sites do keep track of your browsing activities when they serve you content, which enables them to tailor what
        they present to you. You may set your web browser to transmit a "Do Not Track" signal to websites and online
        services you visit. For more information on Do Not Track, please visit <a href="https://www.allaboutdnt.com"
                                                                                  target="_blank">www.allaboutdnt.com</a>.<br>

        <h2>How & Why We Use Your Information</h2>

        The ways we use and disclose your Personal Information as described in this Policy are necessary for our
        legitimate business interests or for reasons related to a contract between you and Impactstory. Our legitimate
        business interests include without limitation, facilitating the Service, evaluating and maintaining the
        functionality and security of the Service, developing and offering additional features and services to benefit
        all Users, and for purposes related to furthering the sales and use of our technology and services. <br>
        <br>
        In situations where we have collected your Personal Infornation, we may use it to: <br>
        <ul type="disc">
            <li>Communicate with you.
                <ul type="circle">
                    <li>For example, we may use your email address to contact you in response to your question or
                        request, solicit feedback from you about the Service, provide technical support and service
                        update notices, and inform you about new features and tools we may offer.
                    </li>
                </ul>
            </li>
            <li>Facilitate and carry out the functions of the Service;</li>
            <li>To carry out our obligations and enforce our rights arising from any contracts entered into between you
                and us, including for billing and collection;
            </li>
            <li>Enforce our Terms of Service and other policies, and preventing prohibited or illegal activities, or
                violations of our Terms of Service;
            </li>
            <li>Improve the Service;
                <ul type="circle">
                    <li>For example, we may identify ways to improve the Service based on your IP address activity or
                        support requests you submit.
                    </li>
                </ul>
            </li>
            <li>To allow you to participate in interactive features on the Site;</li>
            <li>Administer referral programs, rewards, surveys, contests, or other promotional activities or sponsored
                events;
            </li>
            <li>Comply with our legal obligations, resolve any disputes that we may have with any of our users, and
                enforce our agreements;
            </li>
            <li>For any other purpose disclosed to you and to which you consent.</li>
        </ul>

        All Data Feed Subscriptions are subject to the terms of a separate Data Feed Agreement in addition to our Terms
        of Service and this Policy, and we may use your Personal Information in other ways that are related to the Data
        Feed Agreement as described in the Data Feed Agreement.<br>
        <br>
        We use Non-Personal Information to help us to track trends and analyze use patterns on the Site, and to assess
        and improve the Service. Our server logs IP addresses of all requests, but we do not link IP addresses to any
        Personal Information, cookies, usage data, or any other user browsing data. IP addresses are used only for
        technical purposes like server load-balancing or bug fixing. <br>
        <br>
        Impactstory may compile information and data in an aggregated form related to the performance, operation, and
        use of the Service (collectively, "Data Reports"), and may use such Data Reports for security, statistical, or
        operations analyses, and for research and development purposes. Data Reports shall not incorporate any
        information in a form that would identify any individual. Impactstory retains all right, title, and interest in
        and to the Data Reports, including all associated intellectual property rights.<br>

        <h2>How We Disclose Your Information</h2>

        We may disclose any Personal Information or Non-Personal Information you share with us to certain third parties
        for the purpose of facilitating and carrying out the Service, such as the providers of servers for our email
        communications who are provided access to user's email address for purposes of sending emails from us. Those
        vendors use your Personal Information only at our direction and in accordance with our Privacy Policy. We may
        disclose aggregated, anonymous information about our users and any information that does not identify any
        specific individual for business purposes. <br>
        <br>
        We may also disclose your personal information: (1) to comply with any court order, law, or legal process,
        including to respond to any government or regulatory request; (2) to ensure compliance with our Terms of Service
        and other agreements, such as billing; and (3) to protect the rights, property, or safety of Impactstory, our
        customers, or others. Except as otherwise stated in this Policy, we do not sell, trade, rent, or otherwise share
        your Personal Information with third parties without your consent. <br>

        <h2>Data Security</h2>

        We implement security measures designed to protect your information from unauthorized access, use, or
        disclosure. We protect your information from potential security breaches by implementing certain technological
        security measures including encryption, firewalls and secure socket layer technology. However, these measures do
        not guarantee that your information will not be accessed, disclosed, altered or destroyed by breach of such
        firewalls and secure server software. By using our Service, you acknowledge that you understand and agree to
        assume these risks, and Impactstory shall not be responsible for the circumvention of any of the Service's
        privacy settings or security measures. If you have a Data Feed subscription with a unique API key we urge you to
        take steps to keep your API key protected from unauthorized use by others. We may store your information for up
        to six months after you stop using the Service, unless you request that we delete your information sooner in
        accordance with this Policy. <br>

        <h2>Your Rights Related to Your Information</h2>

        Impactstory aims to provide transparency, security, and accessibility when it comes to your personal data. <br>
        <br>
        You may request a copy of your personal data from us at any time. If you request a copy of your data, we will
        provide you a copy in a structured, electronic format. <br>

        You may also request that your personal data be corrected if you believe it is incorrect or incomplete, or that
        it be deleted entirely. We will delete your personal data upon your request if you send us a request with a
        description of the basis for your request; however, we may first restrict processing of your data as permitted
        under applicable law in order to assess your request prior to any deletion of data.<br>
        <br>
        Finally, you may object to our processing of your personal data, or request that we restrict the processing of
        your personal data. If you request that we restrict processing of your data, we will stop processing your data,
        but we may still store your data as permitted under applicable law pending an assessment of your request. <br>
        <br>
        We may not be able to delete your personal data except by also deleting your subscription to the Data Feed, if
        applicable. We may not be able to accommodate a request to change or delete information if we the change would
        violate any law or legal requirement, or if such change would cause the information to be incorrect. <br>
        <br>
        You may revoke consent to our processing activities that are based on your consent, if and when applicable. If
        you withdraw your consent, we may not be able to provide certain products or services to you. We will advise you
        if this is the case at the time you withdraw your consent.<br>
        <br>
        Please direct all requests related to your information to Jason Priem at <a href="mailto:team@ourresearch.org">team@ourresearch.org</a>.
        <br>
        <br>
        If you are a resident of the European Economic Area and you believe we are unlawfully processing your personal
        information, you also have the right to complain to your local data protection supervisory authority. You can
        find their contact details here: <a
            href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm" target="_blank">https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm</a>
        <br>

        <h2>Failure to Provide Personal Information</h2>

        If you fail to provide Personal Information when it is required by law or necessary for us to perform our
        obligations under a contract with you, we may not be able to perform the contract. For example, if you fail to
        provide us with your billing information to pay for a Data Feed Subscription, we may not be able to provide you
        with the Data Feed Subscription you requested.<br>

        <h3>Data Retention</h3>

        In general, we will only retain Personal Information for as long as you are using the Services or as otherwise
        needed to provide you the Services. We may also retain and use your Personal Information as necessary to satisfy
        any legal requirements, including enforcing our rights and agreements and resolving disputes. We will try to
        delete your information promptly consistent with these data retention practices. However, there might be some
        technical delay involved in deleting information from our servers, and backed-up versions might still exist
        after deletion. In addition, we do not delete any data that has been rendered completely anonymous or maintained
        in de-identified, aggregated form with data of other users (such as usage trend reports).<br>
        <br>
        To determine the appropriate retention period for Personal Information, we consider the amount, nature, and
        sensitivity of the personal data, the potential risk of harm from unauthorized use or disclosure of your
        personal data, the purposes for which we process your personal data and whether we can achieve those purposes
        through other means, and the applicable legal requirements. <br>

        <h2>International Transfers of Data</h2>

        Impactstory's principal business operations are conducted in the United States, but the Service may be available
        to users outside the United States. If you are visiting this Site from a country other than the United States,
        your communications with us may result in the transfer of information outside of the country from which are
        reside or use the Service. By visiting the Site, communicating electronically with us, and/or using the Service,
        you consent to any and all such transfers.<br>

        <h2>California Privacy Rights</h2>

        California Civil Code Section &#xA7; 1798.83 permits users of our Service that are California residents to
        request certain information regarding our disclosure of personal information to third parties for their direct
        marketing purposes. To make such a request, please send an e-mail to <a href="mailto:team@ourresearch.org">team@ourresearch.org</a>
        or write us at: 500 Westover Dr #8234, Sanford NC, 27330-8941.<br>

        <h2>Links to Other Websites</h2>

        As part of the Service, we may provide links to or compatibility with other websites or applications. However,
        we are not responsible for the privacy practices employed by those websites or the information or content they
        contain. This Policy applies solely to information collected by us through the Site and the Service. Therefore,
        this Policy does not apply to your use of a third party website accessed by selecting a link on our Site or via
        our Service. To the extent that you access or use the Service through or on another website or application, then
        the privacy Policy of that other website or application will apply to your access or use of that site or
        application. We encourage our users to read the privacy statements of other websites before proceeding to use
        them.<br>

        <h2>Changes to Our Privacy Policy</h2>

        We reserve the right to change this Policy and our Terms of Service at any time. We will notify you of
        significant changes to our Policy by sending a notice to the primary email address specified in your account or
        by placing a prominent notice on our site. Significant changes will go into effect thirty (30) days following
        such notification. Non-material changes or clarifications will take effect immediately. You should periodically
        check the Site and this privacy page for updates.<br>
        <br>
        In the event we undergo a business transaction such as a merger, acquisition by another company, or sale of all
        or a portion of our assets, your Personal Information may be among the assets transferred. You acknowledge and
        consent that such transfers may occur and are permitted by this Privacy Policy, and that any acquirer of our
        assets may continue to process your Personal Information as set forth in this Privacy Policy. <br>

        <h2>Contact Us</h2>

        You have the right to make a complaint at any time to your local data protection authority if you have concerns
        regarding your rights under applicable law. We would, however, appreciate the chance to address your concerns so
        please contact us first so that we can discuss a resolution with you directly.<br>
        <br>
        If you have any questions regarding this Policy or our data processing practices, please contact us by sending
        an email to <a href="mailto:team@ourresearch.org">team@ourresearch.org</a>. <br>
        <br>
        <b>Our full details are:</b><br>
        <b>Full name of legal entity: Impactstory, Inc.</b><br>
        <b>Individual Contact: Jason Priem or Heather Piwowar</b><br>
        <b>Email address: <a href="mailto:team@ourresearch.org">team@ourresearch.org</a></b><br>
        <b>Postal address: 500 Westover Dr #8234 Sanford, NC 27330-8941</b><br>
        <b>Telephone number: <a href="tel:778-848-4724">778-848-4724</a></b><br>


    </div>
</template>

<script>
    export default {
        name: "Privacy",
        metaInfo: {
            title: "Privacy"
        }
    }
</script>

<style scoped lang="scss">

</style>